import {
    Component,
    OnDestroy,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import {
    ColDef,
    GridOptions,
    GridApi,
    GridReadyEvent,
} from 'ag-grid-community';
import { takeUntil, tap, filter } from 'rxjs';
import { nameOf } from '../../../../core/functions/string-helpers';
import { ServiceSelection } from '../../../../core/models/applications/service-selection';
import { AuthenticatedUser } from '../../../../core/models/authenticated-user';
import { FilterOption } from '../../../../core/models/grid/filter-option';
import { IGridComponent } from '../../../../core/models/grid/grid-component';
import { GridHelper } from '../../../../core/models/grid/grid-helper';
import { GridFilterDataService } from '../../../../core/services/grid-filter-data/grid-filter-data.service';
import { ApplicationActionCellRendererComponent } from '../../../../shared/components/grid/cell-renderers/dashboard/application-action-cell-renderer/application-action-cell-renderer.component';
import { ApplicationIdCellRendererComponent } from '../../../../shared/components/grid/cell-renderers/dashboard/application-id-cell-renderer/application-id-cell-renderer.component';
import { GridToolbarComponent } from '../../../../shared/components/grid/grid-toolbar/grid-toolbar.component';
import { GridToolbarOptions } from '../../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import { MultiSelectFilterComponent } from '../../../../shared/components/grid/multi-select-filter/multi-select-filter.component';
import { ApplicationService } from '../../../application/shared/services/application/application.service';
import { ApplicationGridService } from '../../../shared/services/application-grid.service';
import { UserStateService } from '../../../user/shared/services/user-state/user-state.service';
import { DashboardConstants } from '../../constants/dashboard-constants';
import { DashboardSegmentedToggleComponent } from '../dashboard-segmented-toggle/dashboard-segmented-toggle.component';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DashboardSegmentedToggleOption } from '../dashboard-segmented-toggle/models/dashboard-segmented-toggle-option';
import { BaseDashboardComponent } from '../base-dashboard/base-dashboard.component';
import { ServiceOrderCellRendererComponent } from '../../../../shared/components/grid/cell-renderers/service-order-cell-renderer/service-order-cell-renderer.component';
import { ServiceOrderCellRendererParams } from '../../../../shared/components/grid/cell-renderers/service-order-cell-renderer/models/service-order-cell-renderer-params';
import { GridApplicationRowData } from '../../models/grid-application-row-data';
import { ApplicationFilingStatusEnum } from '../../../application/shared/constants/application-filing-status';
import { ServiceSelectionEnum } from '../../../../core/enums/applications/service-selection-enum';
import { ApplicationStatusCellRendererComponent } from '../../../../shared/components/grid/cell-renderers/application-status-cell-renderer/application-status-cell-renderer.component';
import { ApplicationStatusCellRendererParams } from '../../../../shared/components/grid/cell-renderers/application-status-cell-renderer/models/application-status-cell-renderer-params';

@Component({
    selector: 'app-customer-dashboard',
    templateUrl: './customer-dashboard.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        AgGridAngular,
        GridToolbarComponent,
        DashboardSegmentedToggleComponent,
    ],
})
export class CustomerDashboardComponent
    extends BaseDashboardComponent
    implements IGridComponent, OnDestroy
{
    segmentedToggleOptions: DashboardSegmentedToggleOption<string>[] = [];

    private availableSegmentedToggleOptions: DashboardSegmentedToggleOption<string>[] =
        [
            {
                value: DashboardConstants.ALL_APPLICATIONS_FILTER_VALUE,
                label: DashboardConstants.ALL_APPLICATIONS_FILTER_LABEL,
            },
            {
                value: DashboardConstants.WORK_IN_PROGRESS_FILTER_VALUE,
                label: DashboardConstants.CUSTOMER_WORK_IN_PROGRESS_FILTER_LABEL,
            },
            {
                value: DashboardConstants.HANDED_OVER_TO_EWA_FILTER_VALUE,
                label: DashboardConstants.HANDED_OVER_TO_EWA_FILTER_LABEL,
            },
            {
                value: DashboardConstants.COORDINATION_FILTER_VALUE,
                label: DashboardConstants.CUSTOMER_COORDINATION_FILTER_LABEL,
            },
            {
                value: DashboardConstants.OTHER_SERVICES_FILTER_VALUE,
                label: DashboardConstants.OTHER_SERVICES_FILTER_LABEL,
            },
        ];

    private readonly userStateService = inject(UserStateService);
    private readonly filterDataService = inject(GridFilterDataService);

    constructor() {
        super(inject(ApplicationService));
    }

    context: unknown;
    gridDataResponse: GridApplicationRowData[] = [];
    rowData: GridApplicationRowData[] = [];
    gridApi: GridApi | undefined;
    servicesFilterOptions: FilterOption[] = [];

    columnDefs: ColDef[] = [];
    gridToolbarOptions: GridToolbarOptions = {
        ...GridHelper.defaultToolbarOptions,
        exportCsvOptions: {
            columnKeys: [
                nameOf<GridApplicationRowData>('applicationPurpose'),
                nameOf<GridApplicationRowData>('salmonLogId'),
                nameOf<GridApplicationRowData>('callSign'),
                nameOf<GridApplicationRowData>('licenseeEntityName'),
                nameOf<GridApplicationRowData>('coordinatorName'),
                nameOf<GridApplicationRowData>('handedOverToEWAAt'),
                nameOf<GridApplicationRowData>('filingStatus'),
                nameOf<GridApplicationRowData>('doItYourselfAutocoord'),
            ],
            fileName: () =>
                GridHelper.getExportFileName(
                    `${this.form.controls.status.value ?? DashboardConstants.ALL_APPLICATIONS_FILTER_VALUE}_Applications`,
                ),
        },
    };

    gridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
    };

    onGridReady(params: GridReadyEvent<unknown>) {
        this.gridApi = params.api;

        this.userStateService.user$
            .pipe(takeUntil(this.destroy$))
            .subscribe((authUser: AuthenticatedUser | null) => {
                // There could be a brief moment when the auth user is null when masquerade ends.
                // Don't make an API call that we know will result in an exception (no auth user).
                if (authUser) {
                    this.loadDropdownData();
                    this.loadGridData();
                }
            });
    }

    getSegmentedToggleOptions(): DashboardSegmentedToggleOption<string>[] {
        const optionCounts: Record<string, number> = {
            [DashboardConstants.ALL_APPLICATIONS_FILTER_VALUE]: 0,
            [DashboardConstants.WORK_IN_PROGRESS_FILTER_VALUE]: 0,
            [DashboardConstants.HANDED_OVER_TO_EWA_FILTER_VALUE]: 0,
            [DashboardConstants.COORDINATION_FILTER_VALUE]: 0,
            [DashboardConstants.OTHER_SERVICES_FILTER_VALUE]: 0,
        };

        this.gridDataResponse.forEach((data) => {
            let filterMatched: string | undefined = undefined;

            if (this.workInProgressFilter(data)) {
                filterMatched =
                    DashboardConstants.WORK_IN_PROGRESS_FILTER_VALUE;
            }
            if (this.handedOverToEwaFilter(data)) {
                filterMatched =
                    DashboardConstants.HANDED_OVER_TO_EWA_FILTER_VALUE;
            }
            if (this.coordinationFilter(data)) {
                filterMatched = DashboardConstants.COORDINATION_FILTER_VALUE;
            }
            if (this.otherServicesFilter(data)) {
                filterMatched = DashboardConstants.OTHER_SERVICES_FILTER_VALUE;
            }

            if (filterMatched) {
                if (!optionCounts[filterMatched]) {
                    optionCounts[filterMatched] = 0;
                }

                optionCounts[filterMatched]++;
                optionCounts[
                    DashboardConstants.ALL_APPLICATIONS_FILTER_VALUE
                ]++;
            }
        });

        return (
            this.availableSegmentedToggleOptions
                // Only show the "Other Services" filter if there are applications that match that criteria for customers only.
                .filter((option) => {
                    return (
                        option.value !==
                            DashboardConstants.OTHER_SERVICES_FILTER_VALUE ||
                        optionCounts[
                            DashboardConstants.OTHER_SERVICES_FILTER_VALUE
                        ] > 0
                    );
                })
                .map((option) => {
                    const countForOption = optionCounts[option.value];

                    return {
                        ...option,
                        description: `(${countForOption ? countForOption : 0})`,
                    };
                })
        );
    }

    private workInProgressFilter(data: GridApplicationRowData): boolean {
        return (
            data.filingStatus === ApplicationFilingStatusEnum.entryIncomplete &&
            !data.isHandedOver &&
            data.isForm160 === false
        );
    }

    private handedOverToEwaFilter(data: GridApplicationRowData): boolean {
        return data.isHandedOver;
    }

    private coordinationFilter(data: GridApplicationRowData): boolean {
        return (
            data.doItYourselfAutocoord !== ServiceSelectionEnum.GO &&
            !data.isHandedOver &&
            (data.filingStatus === ApplicationFilingStatusEnum.entryComplete ||
                data.filingStatus ===
                    ApplicationFilingStatusEnum.coordinationResultsReady ||
                data.filingStatus ===
                    ApplicationFilingStatusEnum.coordinationSubmitted ||
                data.filingStatus ===
                    ApplicationFilingStatusEnum.coordinationFailed ||
                data.filingStatus ===
                    ApplicationFilingStatusEnum.coordinationProcessing ||
                data.filingStatus ===
                    ApplicationFilingStatusEnum.limitationCheckFailed ||
                data.filingStatus ===
                    ApplicationFilingStatusEnum.autoCoordSubmissionFailed) &&
            data.isForm160 === false
        );
    }

    private otherServicesFilter(data: GridApplicationRowData): boolean {
        return !data.isHandedOver && data.isForm160 === true;
    }

    private allApplicationsFilter(data: GridApplicationRowData): boolean {
        return (
            this.workInProgressFilter(data) ||
            this.handedOverToEwaFilter(data) ||
            this.coordinationFilter(data) ||
            this.otherServicesFilter(data)
        );
    }

    protected filterGridData() {
        const currentStatus = this.form.controls.status.value;

        switch (currentStatus) {
            case DashboardConstants.WORK_IN_PROGRESS_FILTER_VALUE:
                this.rowData = this.gridDataResponse.filter((data) =>
                    this.workInProgressFilter(data),
                );

                break;
            case DashboardConstants.HANDED_OVER_TO_EWA_FILTER_VALUE:
                this.rowData = this.gridDataResponse.filter((data) =>
                    this.handedOverToEwaFilter(data),
                );

                break;
            case DashboardConstants.COORDINATION_FILTER_VALUE:
                this.rowData = this.gridDataResponse.filter((data) =>
                    this.coordinationFilter(data),
                );

                break;
            case DashboardConstants.OTHER_SERVICES_FILTER_VALUE:
                this.rowData = this.gridDataResponse.filter((data) =>
                    this.otherServicesFilter(data),
                );

                break;
            default: {
                this.rowData = this.gridDataResponse.filter((data) =>
                    this.allApplicationsFilter(data),
                );
            }
        }
    }

    protected loadGridData() {
        this.applicationService
            .getAllForDashboard()
            .pipe(
                takeUntil(this.destroy$),
                tap((rowData) => {
                    this.gridDataResponse = rowData;
                    this.filterGridData();
                    this.columnDefs = this.getColumnDefs();
                    this.segmentedToggleOptions =
                        this.getSegmentedToggleOptions();
                }),
                filter(() => !!this.gridApi),
            )
            .subscribe(() => {
                this.gridApi!.setGridOption('rowData', this.rowData);
                this.gridApi!.setGridOption('columnDefs', this.columnDefs);
            });
    }

    private getColumnDefs(): ColDef[] {
        return [
            {
                headerName: DashboardConstants.SERVICES_HEADER_NAME,
                field: nameOf<GridApplicationRowData>('applicationPurpose'),
                cellRenderer: ServiceOrderCellRendererComponent,
                cellRendererParams: (params: ServiceOrderCellRendererParams) =>
                    ({
                        service: ApplicationGridService.formatServicesColumn(
                            params.data,
                        ),
                        orderId: params.data.orderId,
                    }) as ServiceOrderCellRendererParams,
                valueFormatter: (params) => {
                    return ApplicationGridService.formatServicesColumnWithOrder(
                        params.data,
                    );
                },
                valueGetter: (params) => {
                    return ApplicationGridService.getServiceValue(params.data);
                },
                maxWidth: 150,
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions: this.servicesFilterOptions.map((option) => ({
                        label: option.label,
                        value: option.label,
                        selected: option.selected,
                    })),
                },
                sortable: false,
                getQuickFilterText: (params) => {
                    return ApplicationGridService.formatServicesColumnWithOrder(
                        params.data,
                    );
                },
            },
            {
                field: nameOf<GridApplicationRowData>('salmonLogId'),
                headerName: DashboardConstants.APP_ID_PSCA_ID_HEADER_NAME,
                cellRenderer: ApplicationIdCellRendererComponent,
                suppressSizeToFit: true,
                valueGetter: (params) => {
                    return ApplicationGridService.formatApplicationIdColumn(
                        params.data,
                    );
                },
            },
            {
                field: nameOf<GridApplicationRowData>('callSign'),
                headerName: DashboardConstants.CALL_SIGN_FILE_HEADER_NAME,
                cellStyle: {
                    whiteSpace: 'pre-line', // preserves \n characters
                },
                valueGetter: (params) => {
                    return ApplicationGridService.formatCallSignColumn(
                        params.data,
                    );
                },
                suppressSizeToFit: true,
            },
            {
                field: nameOf<GridApplicationRowData>('licenseeEntityName'),
                headerName:
                    DashboardConstants.APPLICANT_TX_LOCATION_HEADER_NAME,
                valueGetter: (params) => {
                    return ApplicationGridService.formatApplicantTransmissionLocationColumn(
                        params.data,
                    );
                },
                minWidth: 250,
            },
            {
                headerName: DashboardConstants.COORDINATOR_HEADER_NAME,
                field: nameOf<GridApplicationRowData>('coordinatorName'),
                minWidth: 150,
            },
            {
                headerName: DashboardConstants.HANDED_OVER_TO_EWA_HEADER_NAME,
                field: nameOf<GridApplicationRowData>('handedOverToEWAAt'),
                ...GridHelper.defaultDateTimeRendererColDef,
                minWidth: 190,
            },
            {
                headerName: DashboardConstants.STATUS_HEADER_NAME,
                field: nameOf<GridApplicationRowData>('filingStatus'),
                cellRenderer: ApplicationStatusCellRendererComponent,
                cellRendererParams: (
                    params: ApplicationStatusCellRendererParams,
                ) =>
                    ({
                        status: params.data.filingStatus,
                        concurrenceDate: params.data.concurrenceDate,
                        releaseDate: params.data.releaseDate,
                    }) as ApplicationStatusCellRendererParams,
                minWidth: 150,
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions:
                        ApplicationGridService.getApplicableApplicationStatusFilterOptions(
                            this.gridDataResponse.filter((data) =>
                                this.allApplicationsFilter(data),
                            ),
                        ),
                },
            },
            {
                headerName: DashboardConstants.ACTION_HEADER_NAME,
                cellRenderer: ApplicationActionCellRendererComponent,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
            },
            {
                field: nameOf<GridApplicationRowData>('doItYourselfAutocoord'),
                headerName: DashboardConstants.SERVICE_SELECTION_HEADER_NAME,
                valueGetter: (params) => {
                    return ApplicationGridService.formatServiceSelectionColumn(
                        params.data,
                    );
                },
                minWidth: 180,
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions:
                        ServiceSelection.getServiceSelectionLabels().map(
                            (label) => ({
                                label: label,
                                value: label,
                                selected: false,
                            }),
                        ),
                },
            },
        ];
    }

    private loadDropdownData() {
        this.filterDataService
            .getApplicationPurposeFilterOptions()
            .pipe(
                takeUntil(this.destroy$),
                tap((options: FilterOption[]) => {
                    this.servicesFilterOptions = [
                        ...options,
                        DashboardConstants.FRN_REQUEST_FILTER_OPTION,
                    ];
                }),
            )
            .subscribe();
    }
}
